import { cn } from '@/@core/utils/cn'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { fadeIn, staggerContainer } from '@/v2/constants/motion'
import { motion } from 'framer-motion'
import React from 'react'

interface HeroSectionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: string
  subTitle?: string
  description?: string
  points?: string[]
  ctaButton: { href: string; text: string }
  btnClassName?: string
}

const HeroSection: React.FC<HeroSectionProps> = props => {
  const { title, description, points, ctaButton, btnClassName, className, ...rest } = props

  return (
    <motion.section
      variants={staggerContainer(0.5, 0)}
      initial='hidden'
      animate='show'
      viewport={{ once: true, amount: 0.25 }}
      className='relative section-container bg-[#C4EDFF]'
    >
      <ResponsiveImage
        src={'/images/v2/pages/main-pages/ski-gear-shipping/hero-image-xl.png'}
        alt={'hero-section-image'}
        className={cn('w-full object-contain hidden lg-mn:block xl-min:!hidden h-full')}
        imgClassName='object-cover h-full'
      />
      <ResponsiveImage
        src={'/images/v2/pages/main-pages/ski-gear-shipping/hero-image-lg.png'}
        alt={'hero-section-image'}
        className={cn('w-full object-contain hidden xl-min:block h-full')}
        imgClassName='object-cover h-full'
      />

      <ResponsiveImage
        src={'/images/v2/pages/main-pages/ski-gear-shipping/hero-image-md.png'}
        alt={'hero-section-image'}
        className={cn('w-full object-contain hidden md-mn:block lg-mn:hidden h-full')}
        imgClassName='object-cover h-full'
      />
      <SectionWrapper
        className={cn(
          'md-mn:absolute md-mn:top-1/2 md-mn:left-1/2 md-mn:-translate-x-1/2 md-mn:-translate-y-1/2 section-spacing',
          className
        )}
        {...rest}
      >
        <div className={cn('flex items-center justify-between flex-col lg-mn:flex-row gap-y-6 md-mn:gap-3')}>
          <motion.div
            variants={fadeIn('right', 'tween', 0, 1)}
            className='flex flex-col justify-center mx-auto w-full [&>h1]:text-center [&>h1]:mx-auto md-mn:[&>h1]:max-w-[301px] lg-mn:[&>h1]:max-w-full [&>h1]:max-w-[312px] [&>h1]:text-heading-home text-center'
          >
            <h1 dangerouslySetInnerHTML={{ __html: title }} />

            {description && (
              <p
                className={cn(
                  'text-paragraph xl-min:text-[28px] xl-min:!mt-8 mt-6 font-normal text-[16px] xl-min:leading-[42px] leading-7 text-center mx-auto md-mn:max-w-[50%]'
                )}
              >
                {description}
              </p>
            )}

            {points && (
              <ul className='list-disc list-inside font-medium  mt-5 leading-[30px]'>
                {points.map(point => (
                  <li key={point} className='text-paragraph font-normal md-mn:text-[20px]'>
                    {point}
                  </li>
                ))}
              </ul>
            )}

            <LinkWithTransition href={ctaButton.href} className='self-center mt-[36px]'>
              <Button
                variant='call_to_action'
                className={cn('transition-all animate-bounce hover:animated-paused', btnClassName)}
              >
                {ctaButton.text}
              </Button>
            </LinkWithTransition>
          </motion.div>
        </div>
      </SectionWrapper>
    </motion.section>
  )
}

export default HeroSection
